/**
 * ============================================================================
 * MAIN JAVASCRIPT ENTRY POINT
 * ============================================================================
 *
 * Main JavaScript file for custom Horizon theme extensions.
 * This file initializes all custom modules and components.
 *
 * @version 2.0.0
 * @author DeSight Studio GmbH
 *
 * ============================================================================
 */

// Note: All imports are removed for browser compatibility
// All classes are available globally after concatenation




/**
 * Main application class
 */
class App {
  constructor() {
    this.eventBus = new EventBus();
    this.modules = new Map();
    this.components = new Map();

    this.init();
  }

  /**
   * Initialize the application
   */
  init() {
    ready(() => {
      // Wait for custom theme settings to load
      if (window.CustomThemeSettings) {
        this.initWithSettings();
      } else {
        document.addEventListener('custom-theme:settings-loaded', () => {
          this.initWithSettings();
        });
      }
    });
  }

  /**
   * Initialize with theme settings
   */
  initWithSettings() {
    this.settings = window.CustomThemeSettings;

    if (window.Shopify && window.Shopify.designMode) {
      console.log('⚙️ Theme settings loaded:', this.settings);
    }

    this.initModules();
    this.initComponents();
    this.bindEvents();
    this.initPerformanceFeatures();

    // Emit app ready event
    this.eventBus.emit('app:ready', { settings: this.settings });

    if (window.Shopify && window.Shopify.designMode) {
      console.log('🚀 DeSight Shopify X v1.0 initialized');
    }
  }

  /**
   * Initialize modules
   */
  initModules() {
    // Theme manager
    this.modules.set('theme', new ThemeManager());

    // Modal manager
    this.modules.set('modal', new ModalManager());

    // Popup manager
    this.modules.set('popup', new PopupManager());

    // Form validator
    this.modules.set('validator', new FormValidator());

    // Minimum order manager
    const minimumOrderManager = new MinimumOrderManager();
    this.modules.set('minimumOrder', minimumOrderManager);

    // Make available globally for debugging
    window.minimumOrderManager = minimumOrderManager;

    // Color system (advanced color features)
    if (this.settings.features?.livePreview || this.settings.features?.contrastChecker ||
        this.settings.features?.colorPaletteGenerator !== 'disabled') {
      this.modules.set('colorSystem', new ColorSystem());
    }

    // Live preview system
    if (this.settings.features?.livePreview) {
      this.modules.set('livePreview', new LivePreview());
    }
  }

  /**
   * Initialize components
   */
  initComponents() {
    // Custom buttons
    document.querySelectorAll('[data-custom-button]').forEach(element => {
      this.components.set(element, new CustomButton(element));
    });

    // Product cards
    document.querySelectorAll('[data-product-card]').forEach(element => {
      this.components.set(element, new ProductCard(element));
    });

    // Navigation menus
    document.querySelectorAll('[data-navigation-menu]').forEach(element => {
      this.components.set(element, new NavigationMenu(element));
    });

    // Mega menu collection grids
    document.querySelectorAll('.mega-menu-collection-grid').forEach(element => {
      this.components.set(element, new MegaMenuCollectionGrid(element));
    });

    // Schmankerl des Monats sections
    if (document.querySelectorAll('.schmankerl-des-monats').length > 0) {
      this.components.set('schmankerl', new SchmankerDesMonats());
    }

    // Bestseller sections
    if (document.querySelectorAll('.section-bestseller').length > 0) {
      this.components.set('bestseller', initBestseller());
    }

    // Collection product cards
    if (document.querySelectorAll('.main-collection').length > 0) {
      this.components.set('collectionProductCards', initCollectionProductCards());
    }

    // Mega menu scroll fix (commented out until implemented)
    // this.components.set('megaMenuScrollFix', new MegaMenuScrollFix());
  }

  /**
   * Bind global events
   */
  bindEvents() {
    // Handle window resize
    window.addEventListener('resize', throttle(() => {
      this.eventBus.emit('window:resize');
    }, 250));

    // Handle scroll
    window.addEventListener('scroll', throttle(() => {
      this.eventBus.emit('window:scroll');
    }, 100));

    // Handle theme changes
    if (window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      mediaQuery.addEventListener('change', (e) => {
        this.eventBus.emit('theme:change', { isDark: e.matches });
      });
    }

    // Handle Shopify events
    document.addEventListener('shopify:section:load', (event) => {
      this.handleSectionLoad(event.detail.sectionId);
    });

    document.addEventListener('shopify:section:unload', (event) => {
      this.handleSectionUnload(event.detail.sectionId);
    });
  }

  /**
   * Handle section load (for theme editor)
   */
  handleSectionLoad(sectionId) {
    const section = document.getElementById(`shopify-section-${sectionId}`);
    if (section) {
      // Re-initialize components in the loaded section
      this.initComponentsInSection(section);
      this.eventBus.emit('section:load', { sectionId, section });
    }
  }

  /**
   * Handle section unload (for theme editor)
   */
  handleSectionUnload(sectionId) {
    // Clean up components in the unloaded section
    this.cleanupComponentsInSection(sectionId);
    this.eventBus.emit('section:unload', { sectionId });
  }

  /**
   * Initialize components in a specific section
   */
  initComponentsInSection(section) {
    // Custom buttons
    section.querySelectorAll('[data-custom-button]').forEach(element => {
      if (!this.components.has(element)) {
        this.components.set(element, new CustomButton(element));
      }
    });

    // Product cards
    section.querySelectorAll('[data-product-card]').forEach(element => {
      if (!this.components.has(element)) {
        this.components.set(element, new ProductCard(element));
      }
    });

    // Navigation menus
    section.querySelectorAll('[data-navigation-menu]').forEach(element => {
      if (!this.components.has(element)) {
        this.components.set(element, new NavigationMenu(element));
      }
    });

    // Mega menu collection grids
    section.querySelectorAll('.mega-menu-collection-grid').forEach(element => {
      if (!this.components.has(element)) {
        this.components.set(element, new MegaMenuCollectionGrid(element));
      }
    });

    // Schmankerl des Monats sections
    if (section.querySelectorAll('.schmankerl-des-monats').length > 0) {
      if (!this.components.has('schmankerl')) {
        this.components.set('schmankerl', new SchmankerDesMonats());
      }
    }

    // Bestseller sections
    if (section.querySelectorAll('.section-bestseller').length > 0) {
      if (!this.components.has('bestseller')) {
        this.components.set('bestseller', initBestseller());
      }
    }

    // Collection product cards
    if (section.querySelectorAll('.main-collection').length > 0) {
      if (!this.components.has('collectionProductCards')) {
        this.components.set('collectionProductCards', initCollectionProductCards());
      }
    }
  }

  /**
   * Clean up components in a specific section
   */
  cleanupComponentsInSection(sectionId) {
    const section = document.getElementById(`shopify-section-${sectionId}`);
    if (section) {
      // Find and destroy components in this section
      this.components.forEach((component, element) => {
        if (section.contains(element)) {
          if (typeof component.destroy === 'function') {
            component.destroy();
          }
          this.components.delete(element);
        }
      });
    }
  }

  /**
   * Get a module by name
   */
  getModule(name) {
    return this.modules.get(name);
  }

  /**
   * Get the event bus
   */
  getEventBus() {
    return this.eventBus;
  }

  /**
   * Initialize performance features based on settings
   */
  initPerformanceFeatures() {
    if (!this.settings) return;

    // Initialize Web Vitals tracking
    if (this.settings.features.webVitalsTracking && typeof initPerformanceMonitoring === 'function') {
      initPerformanceMonitoring();
    }

    // Initialize lazy loading
    if (this.settings.features.lazyLoading && typeof lazyLoadImages === 'function') {
      lazyLoadImages();
    }

    // Preload critical resources
    if (this.settings.features.preloadCriticalResources && typeof ResourceHints === 'function') {
      ResourceHints.preconnect('https://cdn.shopify.com');
      ResourceHints.dnsPrefetch('https://fonts.googleapis.com');
    }

    // Apply performance mode settings
    if (this.settings.features.performanceMode) {
      document.documentElement.classList.add('performance-mode');
    }
  }
}

// Initialize the application
const app = new App();

// Make app globally available for debugging and module access
if (typeof window !== 'undefined') {
  window.CustomThemeApp = app;
  window.DeSightApp = app; // Alternative reference for modules
}


